import type { FC } from 'react';
import { FormField, ProductSelect } from '@george-labs.com/design-system';
import type { ProductItem } from '@george-labs.com/design-system/types/components/Select/ProductSelect';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props extends FormFieldProps {
    products: ProductItem[];
    isErrorVisible?: boolean;
    isOpen?: boolean;
    onSelect?: () => void;
    onClose?: () => void;
    shouldClearOnUnmount?: boolean;
}

const LABEL_COLUMN_WIDTH = 4;

export const FormProductSelectField: FC<Props> = ({
    products,
    label,
    labelPosition,
    fieldName,
    required = true,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    'data-cy': dataCy,
    warning,
    info,
    alert,
    description,
    isErrorVisible = true,
    addon,
    isOpen = false,
    onSelect,
    onClose,
    placeholder,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
}) => {
    const [{ value, onBlur }, { error, touched, dirty }, { setValue, setTouched }] = useField(
        fieldName,
        { shouldClearOnUnmount },
    );

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const handleItemSelect = (selectItem?: ProductItem) => {
        setValue(selectItem?.value);
        setTouched();
        onSelect?.();
    };

    return (
        <FormField
            label={label}
            labelPosition={labelPosition}
            required={required}
            labelColumnWidth={labelColumnWidth}
            data-cy={`${dataCy}-label`}
            error={isFieldErrorVisible && error}
            warning={warning}
            info={info}
            alert={alert}
            input={
                <ProductSelect
                    isOpen={isOpen}
                    value={value}
                    onSelect={(p) => handleItemSelect(p as ProductItem | undefined)}
                    items={products}
                    onClose={onClose}
                    data-cy={`${dataCy}-value`}
                    onBeforeClose={onBlur}
                    placeholder={placeholder}
                />
            }
            description={description}
            addon={addon}
        />
    );
};
